import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import AvatarImg from "../../../img/app/avatar_default_th.jpg";
import { serverUrl, userImagesFolder, thumbSuffix, imageFormat, defaultAvatarImage } from "../../../config/env";
import { toScreen } from "../../../js/modules/Routing";
import PropTypes from "prop-types";

const FlaggedMessagesTab = ({
  issueResolutions,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onReset,
  filters,
  sort,
  onSort,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  inputValue,
  setInputValue,
  navigate,
  flaggedByAI,
}) => {
  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "sorting-icon sorting-icon-up";
    } else if (sort === `${key}=-1`) {
      return "sorting-icon sorting-icon-down";
    }
    return "";
  };

  const getDriverAvatarSrc = useCallback((issueResolution) => {
    if (
      issueResolution?.driverDetails?.avatar &&
      issueResolution?.driverDetails.avatar !== ""
    ) {
      return `${serverUrl}${userImagesFolder}/${issueResolution?.driverDetails?.shortId}/${issueResolution?.driverDetails?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const renderFlaggedMessageRow = (issueResolution, index) => {
    return (
      <tr key={index}>
        <td className="driver-avatar-cell">
          <div className="d-flex align-items-center g-12">
            <img
              src={getDriverAvatarSrc(issueResolution)}
              className="avatar-pic"
              alt="Driver avatar"
              onError={(e) => {
                e.target.src = AvatarImg;
              }}
            />
            <div className="text-capitalize">
              {flaggedByAI?.user?.firstname || "Anonymous"}
              <br />
              <span className="text-sm text-gray-600 ">
                {flaggedByAI?.user?.shortId || "FKCJNPJH6"}
              </span>
            </div>
          </div>
        </td>

        <td>
          <div className="msg-body-wrapper message-container message-driver flagged-ai-suspension">
            <div className="msg-body text-sm">
              Just checking in about tomorrow's move. We are still on for 10 AM,
              right?
            </div>
          </div>

          <div className="flagged-text-suspension text-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
              <path d="M12 9v4"></path>
              <path d="M12 17h.01"></path>
            </svg>
            Flagged due to: {flaggedByAI?.reason || "No reason provided"}
          </div>
        </td>

        <td className="driver-date-cell">
          {moment(issueResolution?.jobDetails?.bookedOn).format("DD-MM-YYYY")}
          <br />
          <span className="text-xs text-gray-600">
            {moment(issueResolution?.jobDetails?.bookedOn).format("HH:mm")}
          </span>
        </td>

        <td>
          <div className="d-inline-flex align-items-center g-8">
            <button type="button" className="custom-btn btn-orange">
              Suspend
            </button>
            <button type="button" className="custom-btn btn-danger">
              Undo Suspension
            </button>
            <button
              type="button"
              className="btn-outline-light text-nowrap"
              onClick={(e) =>
                toScreen(
                  e,
                  navigate,
                  "/conversation/user/" + flaggedByAI?.shortId
                )
              }
            >
              View Conversation
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const columns = useMemo(
    () => [
      { key: "user", label: "User", sortable: true, sortKey: "firstnameSorter" },
      { key: "message", label: "Message", sortable: true, sortKey: "messageSorter" },
      { key: "sentAt", label: "Sent at", sortable: true, sortKey: "sentAtSorter" },
      { key: "actions", label: "", sortable: false },
    ],
    []
  );

  return (
    <>
      <SearchFilterBar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        searchPlaceholder="Client, job ID, or reason..."
        onReset={onReset}
        filters={filters}
      />

      <DataTable
        data={issueResolutions}
        columns={columns}
        renderRow={renderFlaggedMessageRow}
        onSort={onSort}
        getSortClass={getSortClass}
        emptyMessage="No Flagged Messages Found"
        colSpan={4}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={onPageChange}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={onPageSizeChange}
      />
    </>
  );
};

FlaggedMessagesTab.propTypes = {
  issueResolutions: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  sort: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  inputValue: PropTypes.number.isRequired,
  setInputValue: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  flaggedByAI: PropTypes.object,
};

export default FlaggedMessagesTab;
